import { savePropertyValues } from '../api';
import { trackAutosaveSuccess, trackAutosaveUndo } from '../../usage/usageTrackingUtils';
import { bucketFieldCount, shouldLogToConsole, shouldSampleAutosave } from '../../metrics/fieldEditPerfSampling';
import { Metrics } from '../../metrics/Metrics';
import Raven from 'raven-js';
function getIdentifiers(batch) {
  return [...new Set(Object.values(batch.updates).flatMap(update => Object.keys(update)))];
}
export const createPropertyEntityStrategy = (httpClient, onObjectsUpdated) => ({
  type: 'PROPERTY',
  createBatches(items) {
    const batches = {};
    for (const item of items) {
      const {
        request,
        onSuccess,
        onFailure,
        onUndo
      } = item.data;
      const {
        objectTypeId,
        objectId,
        updates
      } = request;
      const {
        properties,
        previousProperties
      } = updates;
      if (!batches[objectTypeId]) {
        batches[objectTypeId] = {
          type: 'PROPERTY',
          objectTypeId,
          updates: {},
          previousUpdates: {},
          successCallbacks: [],
          failureCallbacks: [],
          undoCallbacks: []
        };
      }
      if (!batches[objectTypeId].updates[objectId]) {
        batches[objectTypeId].updates[objectId] = {};
      }
      if (!batches[objectTypeId].previousUpdates[objectId]) {
        batches[objectTypeId].previousUpdates[objectId] = {};
      }
      properties.forEach(({
        name,
        value
      }) => {
        batches[objectTypeId].updates[objectId][name] = value;
      });
      previousProperties.forEach(({
        name,
        value
      }) => {
        batches[objectTypeId].previousUpdates[objectId][name] = value;
      });
      batches[objectTypeId].successCallbacks.push(onSuccess);
      batches[objectTypeId].failureCallbacks.push(onFailure);
      batches[objectTypeId].undoCallbacks.push(onUndo);
    }
    return Object.values(batches);
  },
  async save(items) {
    const batches = this.createBatches(items);
    const allResults = await Promise.allSettled(batches.map(async batch => {
      const {
        objectTypeId,
        updates
      } = batch;
      const identifiers = getIdentifiers(batch);
      const result = await savePropertyValues(objectTypeId, updates, httpClient).catch(error => {
        batch.failureCallbacks.forEach(callback => callback(error));
      });
      try {
        if (result) {
          var _this$onSaveSuccess;
          trackAutosaveSuccess({
            type: 'PROPERTY',
            identifiers,
            objectTypeId
          });
          Metrics.counter('autosave-property-success', {
            propertyCount: String(identifiers.length)
          }).increment();
          batch.successCallbacks.forEach(callback => callback(result));
          (_this$onSaveSuccess = this.onSaveSuccess) === null || _this$onSaveSuccess === void 0 || _this$onSaveSuccess.call(this, result, objectTypeId);
          if (shouldSampleAutosave()) {
            const callbacksDone = performance.now();
            requestAnimationFrame(() => {
              const renderDone = performance.now();
              const isTabVisible = document.visibilityState === 'visible';
              identifiers.forEach(name => {
                const markName = `field-edit-perf:${name}`;
                const entries = performance.getEntriesByName(markName, 'mark');
                if (entries.length > 0) {
                  const mark = entries[entries.length - 1];
                  const saveStart = mark.startTime;
                  const detail = mark.detail;
                  if (isTabVisible) {
                    var _detail$fieldCount, _detail$inputVariant, _detail$type, _detail$fieldType;
                    const fieldType = detail ? `${detail.type}/${detail.fieldType}` : 'unknown';
                    const fieldCount = (_detail$fieldCount = detail === null || detail === void 0 ? void 0 : detail.fieldCount) !== null && _detail$fieldCount !== void 0 ? _detail$fieldCount : '?';
                    const inputVariant = (_detail$inputVariant = detail === null || detail === void 0 ? void 0 : detail.inputVariant) !== null && _detail$inputVariant !== void 0 ? _detail$inputVariant : 'unknown';
                    const metricTags = {
                      type: (_detail$type = detail === null || detail === void 0 ? void 0 : detail.type) !== null && _detail$type !== void 0 ? _detail$type : 'unknown',
                      fieldType: (_detail$fieldType = detail === null || detail === void 0 ? void 0 : detail.fieldType) !== null && _detail$fieldType !== void 0 ? _detail$fieldType : 'unknown',
                      fieldCount: bucketFieldCount(Number(fieldCount) || 0),
                      inputVariant
                    };
                    const metricValue = renderDone - saveStart;
                    Metrics.timer('field-edit-perf-autosave-render', metricTags).update(metricValue);
                    if (shouldLogToConsole()) {
                      // eslint-disable-next-line no-console
                      console.log(`[field-edit-perf] autosave ${name} (${fieldType}, ${fieldCount} fields, ${inputVariant}):`, `trigger→callbacks=${(callbacksDone - saveStart).toFixed(1)}ms`, `trigger→render=${metricValue.toFixed(1)}ms`, `| metric: field-edit-perf-autosave-render`, `value=${metricValue.toFixed(1)}ms`, `tags=`, metricTags);
                    }
                  }
                  performance.clearMarks(markName);
                }
              });
            });
          }
        }
      } catch (e) {
        Metrics.counter(`autosave-property-onsave-failure`).increment();
        Raven.captureException(new Error('Autosave property onsave failure'), {
          extra: {
            objectTypeId,
            identifiers,
            originalError: e
          }
        });
      }
      return result;
    }));
    return allResults.every(result => result.status === 'fulfilled' && result.value);
  },
  async undo(items) {
    const batches = this.createBatches(items);
    const allResults = await Promise.allSettled(batches.map(async batch => {
      const {
        objectTypeId,
        previousUpdates,
        updates,
        failureCallbacks
      } = batch;
      const identifiers = getIdentifiers(Object.assign({}, batch, {
        previousUpdates: updates,
        updates: previousUpdates
      }));
      trackAutosaveUndo({
        type: 'PROPERTY',
        identifiers,
        objectTypeId
      });
      const result = await savePropertyValues(objectTypeId, previousUpdates, httpClient).catch(error => {
        failureCallbacks.forEach(callback => callback(error));
        Metrics.counter('autosave-property-undo-failure').increment();
      });
      Metrics.counter('autosave-property-undo-success').increment();
      try {
        if (result) {
          var _this$onSaveSuccess2;
          (_this$onSaveSuccess2 = this.onSaveSuccess) === null || _this$onSaveSuccess2 === void 0 || _this$onSaveSuccess2.call(this, result, objectTypeId);
          batch.undoCallbacks.forEach(callback => callback(result));
        }
      } catch (e) {
        Metrics.counter('autosave-property-undo-callback-failure').increment();
        Raven.captureException(new Error('Autosave undo handler failed'), {
          extra: {
            objectTypeId,
            identifiers,
            originalError: e
          }
        });
      }
      return result;
    }));
    return allResults.every(result => result.status === 'fulfilled' && result.value);
  },
  onSaveSuccess: onObjectsUpdated
});